import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { FiClock, FiCheckCircle, FiXCircle, FiFilter, FiTrash2 } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { applicationsApi } from '../../utils/api';
import './MyApplicationsPage.css';

const MyApplicationsPage = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery(
    ['my-applications', statusFilter, page],
    () => applicationsApi.getMy({ status: statusFilter || undefined, page, limit: 10 }),
    { keepPreviousData: true }
  );

  const withdrawMutation = useMutation(
    (id) => applicationsApi.withdraw(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('my-applications');
      }
    }
  );

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'ממתין', icon: FiClock, className: 'status-pending' };
      case 'approved':
        return { label: 'אושר', icon: FiCheckCircle, className: 'status-approved' };
      case 'rejected':
        return { label: 'נדחה', icon: FiXCircle, className: 'status-rejected' };
      case 'withdrawn':
        return { label: 'בוטל', icon: FiXCircle, className: 'status-withdrawn' };
      default:
        return { label: status, icon: FiClock, className: '' };
    }
  };

  const statusOptions = [
    { value: '', label: 'הכל' },
    { value: 'pending', label: 'ממתין' },
    { value: 'approved', label: 'אושר' },
    { value: 'rejected', label: 'נדחה' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleWithdraw = async (id) => {
    const result = await Swal.fire({
      title: 'ביטול הגשה',
      text: 'האם אתה בטוח שברצונך לבטל את ההגשה?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'כן, בטל הגשה',
      cancelButtonText: 'חזור',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      withdrawMutation.mutate(id);
    }
  };

  const applications = data?.data?.applications || [];
  const pagination = data?.data?.pagination || {};

  return (
    <div className="my-applications-page">
      <div className="page-header">
        <h1>ההגשות שלי</h1>
        <p>עקוב אחרי סטטוס ההגשות שלך לקמפיינים</p>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <FiFilter className="filter-icon" />
        <select
          className="form-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Applications List */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <h3>אין הגשות להצגה</h3>
          <p>עדיין לא הגשת מועמדות לקמפיינים</p>
          <Link to="/campaigns" className="btn btn-primary">
            גלה קמפיינים
          </Link>
        </div>
      ) : (
        <div className="applications-list">
          {applications.map((application, index) => {
            const statusInfo = getStatusInfo(application.status);
            const StatusIcon = statusInfo.icon;

            return (
              <motion.div
                key={application.id}
                className="application-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="application-image">
                  {application.cover_image_url ? (
                    <img src={application.cover_image_url} alt={application.campaign_title} />
                  ) : application.brand_logo_url ? (
                    <div className="placeholder">
                      <img src={application.brand_logo_url} alt={application.brand_name} />
                    </div>
                  ) : (
                    <div className="placeholder" />
                  )}
                </div>

                <div className="application-content">
                  <div className="application-header">
                    <span className="brand-name">{application.brand_name || '-'}</span>
                    <span className={`status-badge ${statusInfo.className}`}>
                      <StatusIcon />
                      {statusInfo.label}
                    </span>
                  </div>

                  <h3 className="campaign-title">
                    <Link to={`/campaigns/${application.campaign_id}`}>
                      {application.campaign_title || 'קמפיין'}
                    </Link>
                  </h3>

                  {application.short_description && (
                    <p className="campaign-description">{application.short_description}</p>
                  )}

                  <div className="application-meta">
                    <span>הוגש: {formatDate(application.created_at)}</span>
                    {application.updated_at && application.updated_at !== application.created_at && (
                      <span>עודכן: {formatDate(application.updated_at)}</span>
                    )}
                  </div>

                  {application.status === 'pending' && (
                    <div className="application-actions">
                      <button
                        className="btn btn-outline btn-sm btn-danger"
                        onClick={() => handleWithdraw(application.id)}
                        disabled={withdrawMutation.isLoading}
                      >
                        <FiTrash2 /> בטל הגשה
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`pagination-btn ${page === p ? 'active' : ''}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyApplicationsPage;
